import { useState } from "react";
import {
  Alert,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Percent } from "@mui/icons-material";
import { Button, useNotify, useRefresh } from "react-admin";
import { apiUrl, httpClient } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import type { Environment } from "../../../types/api";

/**
 * Sets one markup on every hotel provider (ratehawk, hotelbeds, ggt) for an
 * environment via PUT /admin/providers/business-rules/markup. Flights are not
 * touched. A single provider can still be overridden on its Business Rules tab.
 */
export const SetAllMarkupButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState("7");
  const [environment, setEnvironment] = useState<Environment>("production");
  const [saving, setSaving] = useState(false);

  const value = Number(percent);
  const valid =
    percent !== "" && Number.isFinite(value) && value >= 0 && value <= 100;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await httpClient(apiUrl("providers/business-rules/markup"), {
        method: "PUT",
        body: JSON.stringify({ markupPercentage: value / 100, environment }),
        headers: new Headers({ "Content-Type": "application/json" }),
      });
      notify(
        `Markup set to ${value}% for RateHawk, HotelBeds and GGT (${environment})`,
        { type: "success" },
      );
      setOpen(false);
      refresh();
    } catch (error) {
      notify(handleApiError(error).message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button label="Set markup for all" onClick={() => setOpen(true)}>
        <Percent />
      </Button>
      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Markup for all hotel providers</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "8px !important",
          }}
        >
          <TextField
            label="Markup (%)"
            type="number"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            error={!valid}
            helperText={
              valid
                ? "Customers' normal price; personal user markups can only lower it."
                : "Enter 0–100"
            }
            slotProps={{ htmlInput: { min: 0, max: 100, step: 0.5 } }}
            autoFocus
          />
          <FormControl fullWidth>
            <InputLabel id="set-all-markup-env">Environment</InputLabel>
            <Select
              labelId="set-all-markup-env"
              label="Environment"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as Environment)}
            >
              <MenuItem value="production">Production</MenuItem>
              <MenuItem value="development">Development</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="info">
            Applies to RateHawk, HotelBeds and GGT. Flights are unchanged.
            Override one provider afterwards on its Business Rules tab.
          </Alert>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={save}
            disabled={!valid || saving}
          >
            {saving ? "Saving…" : "Apply to all"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
